import React from 'react';
import {
  always,
  cond,
  equals,
  filter,
  flatten,
  map,
  pathOr,
  T,
  includes,
  propEq,
  find,
  not,
  join,
  toUpper,
  uniq,
  isEmpty,
} from 'ramda';
import {
  _createAdditionalLoans,
} from 'src/features/Advisors/Applications/SummaryMortgage/ShowAll/popupsContext';
import {
  SHOW_VOD,
  SHOW_VOM,
  SHOW_VOR,
  SHOW_VOE,
  SHOW_VOL,
  SHOW_VOOA,
  SHOW_VOOL,
  SHOW_OTHER_INCOMES,
  SHOW_VOAL,
  SHOW_VOGG,
  SHOW_MORE_COLLATERAL,
  SHOW_FEES_AND_CHARGES,
  SHOW_DISBURSEMENTS,
} from 'src/features/Advisors/Applications/SummaryMortgage/ShowAll/ShowAllContext';
import {
  doNothing,
  LABELS,
  OBJECT_META
} from 'src/services/constants';

import { isEmptyOrNil, isNotEmpty, returnIsNotEmptyOrTrue } from 'src/utilities/hasValue';
import { ADVISOR_DASHBOARD_PRODUCT_TYPE_VISIT } from 'src/features/RBAC/Rules/permissionData';
import { getMultiplePermissionOptions } from 'src/features/RBAC/permissions';
import { DISPLAYER_MORE_COLLATORAL_OWNERS_LIST } from './constants';
import { SingleRuleApply } from './displayers/configRules';
import { check } from 'src/features/RBAC/Can';
import { HOME, MORTGAGE_PRODUCTS, PERSONAL_REVOLVING_LINE_OF_CREDIT, UNSECURED } from 'src/features/LoansHome/Products/constants';


export const removeUpdatedAPIs = (formType: string, updatedAPIs: any) =>
  filter(
    (resource: string) =>
      not(equals(getApiTypeFromPopupType(formType), resource)),
    updatedAPIs
  );

export const getApiTypeFromPopupType = (popupType: string) =>
  cond([
    [equals(SHOW_VOR), always('borrowers')],
    [equals(SHOW_VOD), always('assests')],
    [equals(SHOW_VOOA), always('other_assets')],
    [equals(SHOW_VOL), always('liabilities')],
    [() => includes(popupType, [SHOW_FEES_AND_CHARGES, SHOW_DISBURSEMENTS]), always('lending_terms')],
    [equals(SHOW_VOOL), always('other_liabilities')],
    [equals(SHOW_VOM), always('real_estate_asset')],
    [equals(SHOW_VOE), always('employments')],
    [equals(SHOW_MORE_COLLATERAL), always('collateral')],
    [equals(DISPLAYER_MORE_COLLATORAL_OWNERS_LIST), always('collateral')],
    [equals(SHOW_OTHER_INCOMES), always('monthly_incomes')],
    [equals(SHOW_VOAL), always('additional_loan')],
    [equals(SHOW_VOGG), always('gifts_and_grants')],
    [T, always('')],
  ])(popupType);

/*
 mutateProduct filters the given loan product and its sub products
 If we don't have match will send first product which we have in loanProducts
*/
export const mutateProduct = (
  loanProducts: any,
  loanType: string,
  allowAllProducts?: boolean,
  hasMultipleLoan?: boolean,
  productId?: any,
  hasLender?: boolean
) => {

  //filtering products based on product id
  const matchedProducts = filter(
    (loanProduct: any) =>
      equals(pathOr('', ['product_name'], loanProduct), loanType) &&
      equals(pathOr('', ['id'], loanProduct), productId),
    loanProducts
  );
  const showDescription = pathOr(
    'true',
    [0, 'metadata', 'show_description'],
    isNotEmpty(matchedProducts) ? matchedProducts : loanProducts
  );

  const subProducts = getSubProduct(
    isNotEmpty(matchedProducts) ? matchedProducts : loanProducts
  );

  const products = allowAllProducts
    ? subProducts
    : filter(
      (item: any) => hasLender ? (!equals('true', pathOr('', ['inactive'], item))
        || equals('lender', pathOr('', ['show_product_on'], item))) : !equals('true', pathOr('', ['inactive'], item)),
      subProducts
    );
  const updProducts = hasMultipleLoan ? subProducts : products;

  return mutateSubProduct(updProducts, showDescription)
};

/**
 * @param subProducts List of sub products 
 * @param showDescription boolean string send discription name for a product or not
 * @returns 
 */
export const mutateSubProduct = (subProducts: any, showDescription?: string) =>
  map((subproduct: any) => {
    const name = pathOr('', ['name'], subproduct);
    // TODO: we should replace with alias config 
    const display_label = pathOr(name, ['services', 'applicant_get_started_alias_name'], subproduct);
    const value = pathOr('', ['update_it_as'], subproduct) || name;

    return {
      label: name,
      value: value,
      display_label,
      aliasname: pathOr('', ['aliasname'], subproduct),
      description: equals('true', showDescription)
        ? pathOr('', ['description'], subproduct)
        : '',
      loan_type: toUpper(pathOr('', ['aliasname'], subproduct)),
      hide_product_on: pathOr([], ['hide_product_on'], subproduct)
    };
  }, subProducts);


export const getSubProduct = (loanProducts: any) =>
  flatten(map((x) => pathOr([], ['subproducts'], x), loanProducts));

/**
 * filterPermissionProducts is used to filter the base products which enabled for the given role's permission
 * in case there is no permission for the product restriction we consider all products allowed for the role 
 *  @param { list } of permissions of a role
 *  @param { list } of loanproducts for a namespace
 * returns allowed based products 
*/
export const filterPermissionProducts = (permissions: any, loanProducts: any) => {
  const { productTypes } = getProductTypes(permissions, loanProducts)
  const loanProductPermission = find(
    propEq('name', ADVISOR_DASHBOARD_PRODUCT_TYPE_VISIT),
    permissions
  );
  if (isEmptyOrNil(loanProductPermission)) {
    return loanProducts
  }
  const productNames = uniq(map(pathOr('', ['loan_type']), productTypes))
  return filter((loanProduct: any) => {
    return includes(pathOr('', ['product_name'], loanProduct), productNames)
  }, loanProducts)
}

/**
 * this function is used to indentify the view name and use that name for product instead of the default name.
 */
export const getSiteViewProductName = (subProduct: any, siteViewName: string) => {
  const subProductName = pathOr('', ['name'], subProduct)
  const siteViewAliasNames = pathOr([], ['services', 'site_application_view', 'aliases_displays'], subProduct)
  const productNameAlias = find(
    (alias: any) => includes(siteViewName, alias.view_identifiers || []),
    filter(propEq('field', 'name'), siteViewAliasNames)
  )
  const aliasLabel = pathOr('', ['display_it_as'], productNameAlias)
  return returnIsNotEmptyOrTrue(
    aliasLabel,
    aliasLabel,
    subProductName
  )
}

const _getSiteViewProductName = (productName: string, subProducts: any[], siteViewName: string): string => {
  if (isEmptyOrNil(siteViewName)) {
    return productName
  }
  const product = find(propEq('name', productName), subProducts);
  return getSiteViewProductName(product, siteViewName)
}

export const getCollateralOrProductType = (
  collateralType: string,
  productType: string,
  subProductOrAliasName: string
) => {
  return cond([
    [() => includes(productType, MORTGAGE_PRODUCTS), always(HOME)],
    [() => isNotEmpty(collateralType), always(collateralType)],
    [() => equals(productType, PERSONAL_REVOLVING_LINE_OF_CREDIT) && isEmpty(collateralType), always(UNSECURED)],
    [T, always(subProductOrAliasName || productType)],
  ])();
};

/**
 * getProductTypes is used to filter the sub-products which enabled for the given role's permission
 * in case there is no permission for the product restriction we consider all sub-products allowed for the role 
 * @param { list } of permissions of a role
 * @param { list } of loanproducts for a namespace
 * returns {
 *    productTypes: allowed based sub-products on permission
 *   productNames: sub-products names as comma separated string
 * }  
*/
export const getProductTypes = (
  permissions: any, 
  loanProducts: any,
  shouldShowInternalName: boolean = false,
  siteViewName: string = ''
) => {
  const permissionProducts = getMultiplePermissionOptions(permissions, ADVISOR_DASHBOARD_PRODUCT_TYPE_VISIT)

  const productTypes = isNotEmpty(permissionProducts)
    ? permissionProducts
    : mutateProduct(loanProducts, '', false, false, '', true);

  const productNames = join(
    ',',
    map((item: any) => pathOr('', ['value'], item), productTypes)
  );
  
  const products = getSubProduct(loanProducts)
  if (shouldShowInternalName || isNotEmpty(siteViewName)) {
    return {
      productTypes: map((item: any) => {
        const aliasLabel = _getSiteViewProductName(pathOr('', ['label'], item), products, siteViewName)
        return {
          ...item,
          label: aliasLabel
        };
      }, productTypes),
      productNames
    };
  }
  return { productTypes, productNames };
};

export const getEmploymentByType = (employments: any, employmentType: any) => {
  return find(
    (item: any) =>
      equals(
        pathOr('', [OBJECT_META, LABELS, 'type_of_employment'], item),
        employmentType
      ),
    employments
  );
};

export const ApplicationInfoTabsContext = React.createContext({
  editingDisabled: true,
  popupSubmitting: false,
  saveDisabled: true,
  handleAddItem: doNothing as any, // any is for allowing any number of parameters
  handleShowAllSave: doNothing as any,
  handleShowAllCancel: doNothing as any,
  handleRemoveItem: doNothing() as any,
  setRole: doNothing as any,
  handleSelectAddress: doNothing() as any,
  copyFromBorrower: doNothing() as any,
});

//this method will return permission options config for the permission
export const getPermissionConfig = (permissionName: string, permissions: any) => {
  const options = pathOr(
    [],
    ['options'],
    find(propEq('name', permissionName), permissions)
  );
  return {
    eventContext: pathOr([], ['event_context'], options),
    ownerSelection: pathOr('', ['owner_selection'], options),
    loanPermission: pathOr('', ['loan_permission'], options),
    mandatoryFields: pathOr('', ['mandatory_fields'], options),
    loanStatus: pathOr({}, ['loan_status'], options),

  }
}

/**
 * 
 * @param branches list of branches for the given namespace
 * @returns in {label:`0383 - Albany GA`,value:`0383`} to be displayed on dropdown
 */
export const mkBranchCodes = (branches: any) => {
  return map((branch) => {
    const branchName = pathOr("", ["name"], branch)
    const code = pathOr("", ["code"], branch)
    return {
      label: `${code} - ${branchName}`,
      value: code,
    }
  }, branches)

}

// enable function based on loan status
export const shouldShowAction = (
  actionPermission: any,
  allPermissions: any,
  loanStatus: any
) => {
  const permissionName = pathOr('', ['permissionName'], actionPermission)
  const { loanStatus: rules }: any = getPermissionConfig(
    permissionName,
    allPermissions
  )

  const shouldShow = isNotEmpty(rules) ? new SingleRuleApply([], rules).stageMatch(loanStatus) :
    check(allPermissions, permissionName, {})
  return shouldShow;
}